// src/lib/resources/types/ebooks/index.ts

/**
 * @fileoverview Type definitions for ebook resources. Defines interfaces and types
 * for ebook content, chapters, sections and related metadata.
 * @module resources/types/ebooks
 */

import type { BaseResource } from '../shared/base'
import type { BaseContentSection } from '../shared/sections'
import type { TemplateAccess } from '../templates/base'
import { ResourceType } from '../shared/enums'
import type { EbookContentBlock } from './content-blocks'

export type { EbookContentBlock } from './content-blocks'
export type {
  EbookParagraphBlock,
  EbookCalloutBlock,
  EbookTableBlock,
  EbookFormulaBlock,
  EbookStepsBlock,
  EbookChecklistBlock,
  EbookScenarioBlock,
} from './content-blocks'

/**
 * Ebook chapter interface representing a major content division
 * @interface Chapter 
 * @property {string} title - Chapter title
 * @property {string} description - Chapter description/summary
 * @property {EbookSection[]} sections - Chapter content sections
 * @property {number} order - Chapter ordering/sequence number
 */
export interface Chapter {
    title: string
    description: string
  sections: EbookSection[]
  order: number
}

/**
 * Ebook section interface extending base content section
 * @interface EbookSection
 * @extends {BaseContentSection}
 * @property {string} [summary] - Optional section summary
 * @property {string[]} [keyTakeaways] - Key points from the section
 * @property {string[]} [citations] - Reference citations
 */
export interface EbookSection extends BaseContentSection {
  summary?: string
  keyTakeaways?: string[]
  citations?: string[]
  /** Structured body blocks for PDF and expanded on-page previews */
  blocks?: EbookContentBlock[]
}

/**
 * Complete ebook resource interface
 * @interface Ebook
 * @extends {BaseResource}
 * @property {'ebook'} type - Resource type identifier
 * @property {string} author - Ebook author name
 * @property {string} publishDate - Publication date
 * @property {Chapter[]} chapters - Ebook chapters
 * @property {string[]} [topics] - Main topics covered
 * @property {string} [downloadUrl] - URL for downloadable version
 * @property {string} [coverImage] - Cover image URL
 * @property {TableOfContents} [tableOfContents] - Table of contents
 */
/**
 * Landing-page content block for indexable on-page copy.
 *
 * @interface EbookLandingSection
 * @property {string} id - Section anchor id
 * @property {string} title - Section heading
 * @property {string} content - Free on-page body copy
 */
export interface EbookLandingSection {
  id: string
  title: string
  content: string
}

/**
 * Download + gating configuration for the PDF companion.
 *
 * @interface EbookConfig
 * @property {Object} download - Downloadable PDF settings
 * @property {string} download.contentUrl - Public path to the PDF file. NOTE:
 *   this is a publicly downloadable static asset, not an access-controlled
 *   resource — `isGated` only drives a lead-capture nudge, it does not protect
 *   the file (see `EbookViewerWrapper`).
 * @property {string} download.previewUrl - Preview image shown before download
 * @property {boolean} download.isGated - Whether to show the email lead-capture
 *   nudge before opening the (publicly accessible) PDF. Not a hard gate.
 * @property {TemplateAccess} access - Email lead-capture configuration
 */
export interface EbookConfig {
  download: {
    contentUrl: string
    previewUrl: string
    isGated: boolean
  }
  access: TemplateAccess
}

export interface Ebook extends BaseResource {
  resourceType: 'ebook'
  author: string
  publishDate: string
  chapters: Chapter[]
  topics?: string[]
  downloadUrl?: string
  coverImage?: string
  tableOfContents?: TableOfContents
  /** Approximate PDF page count for structured data */
  pageCount?: number
  /** PDF byte size for schema.org `contentSize` */
  fileSizeBytes?: number
  /** Human-readable file size label for UI (e.g. "2.4 MB") */
  fileSize?: string
  /** Indexable landing sections (overview, who it's for, etc.) */
  sections?: EbookLandingSection[]
  /** Download and email-gating configuration */
  config?: EbookConfig
}

/**
 * Type guard for ebook resources.
 *
 * @param {unknown} value - Value to check
 * @returns {boolean} True when value is a valid Ebook
 */
export function isEbook(value: unknown): value is Ebook {
  if (typeof value !== 'object' || value === null) return false
  const ebook = value as Partial<Ebook>
  return (
    ebook.resourceType === ResourceType.EBOOK &&
    typeof ebook.author === 'string' &&
    typeof ebook.publishDate === 'string' &&
    Array.isArray(ebook.chapters)
  )
}

/**
 * Table of contents interface for ebook navigation
 * @interface TableOfContents
 * @property {Array<TOCEntry>} entries - TOC entries
 */
export interface TableOfContents {
  entries: Array<TOCEntry>
  }

/**
 * Table of contents entry interface
 * @interface TOCEntry
 * @property {string} title - Entry title
 * @property {string} [anchor] - HTML anchor link
 * @property {number} [page] - Page number
 * @property {TOCEntry[]} [children] - Nested entries
 */
export interface TOCEntry {
  title: string
  anchor?: string
  page?: number
  children?: TOCEntry[]
}
